import { createContext, useContext, useMemo } from 'react';
import { create } from 'zustand';
import { useBreakpoints } from '../hooks/useBreakpoints';

type ChatContextType = {
  currentActiveChat: string;
  setCurrentActiveChat(_chatId: string): void;
  isDrawer: boolean;
  setIsDrawer(_isDrawer: boolean): void;
  isCompact: boolean;
  goToMessage: string | null;
  setGoToMessage: (messageId: string | null) => void;
};

const chatContextState = create<ChatContextType>((set) => ({
  currentActiveChat: '',
  setCurrentActiveChat: (currentActiveChat) => set({ currentActiveChat }),
  isDrawer: false,
  setIsDrawer: (isDrawer) => set({ isDrawer }),
  isCompact: false,
  goToMessage: null,
  setGoToMessage: (goToMessage) => set({ goToMessage }),
}));

export const ChatContext = createContext<ChatContextType | null>(null);

type Props = {
  children: React.ReactNode;
};

export function ChatContextProvider({ children }: Props) {
  const useStore = useMemo(() => chatContextState, []);

  const store = useStore();

  const { isMobile } = useBreakpoints();

  const isCompact = useMemo(
    () => isMobile || store.isDrawer,
    [store.isDrawer, isMobile],
  );

  const memoizedValue = useMemo(
    () => ({
      ...store,
      isCompact,
    }),
    [store, isCompact],
  );

  return (
    <ChatContext.Provider value={memoizedValue}>
      {children}
    </ChatContext.Provider>
  );
}

export function useChatContext() {
  const context = useContext(ChatContext);

  if (!context)
    throw new Error('useChatContext must be use inside ChatContextProvider');

  return context;
}
